/**
 * Client-side crypto for Cerium's payment PIN/OTP security model.
 *
 * Check n26/monosan's `docs/service/payment-2-factor-authentication.md`
 * for more information on the protocol and its security rationale.
 */

import forge from 'node-forge'

// GET /api/encryption/key's real response is bare base64 DER (SPKI), with
// no PEM armor (`-----BEGIN PUBLIC KEY-----`)
// Stripping any armor that IS present (if the backend ever adds it) then
// parsing raw DER directly via forge's ASN.1 layer handles both shapes,
// rather than assuming full PEM text as `forge.pki.publicKeyFromPem` does.
const importPublicKey = (pemOrDer: string): forge.pki.rsa.PublicKey => {
  const stripped = pemOrDer
    .replace(/-----BEGIN [^-]+-----/g, '')
    .replace(/-----END [^-]+-----/g, '')
    .replace(/\s+/g, '')
  const der = forge.util.decode64(stripped)
  const asn1 = forge.asn1.fromDer(der)
  return forge.pki.publicKeyFromAsn1(asn1) as forge.pki.rsa.PublicKey
}

const toBase64 = (buf: ArrayBuffer): string => {
  let binary = ''
  const bytes = new Uint8Array(buf)
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary)
}

const toHex = (buf: ArrayBuffer): string =>
  Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')

//** HMAC-SHA256(key, message), hex-encoded. Used for `paymentHash` */
export const hmacSha256Hex = async (
  key: string,
  message: string
): Promise<string> => {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  const signature = await crypto.subtle.sign(
    'HMAC',
    cryptoKey,
    new TextEncoder().encode(message)
  )
  return toHex(signature)
}

/** A fresh AES-128-CBC key + 16-byte IV, generated once per payment. */
export const generateAesKeyAndIv = async (): Promise<{
  key: CryptoKey
  iv: Uint8Array<ArrayBuffer>
}> => {
  const key = await crypto.subtle.generateKey(
    { name: 'AES-CBC', length: 128 },
    true,
    ['encrypt']
  )
  const iv = crypto.getRandomValues(new Uint8Array(16))
  return { key, iv }
}

/** AES-128-CBC-encrypts a short string with the given key+IV, base64-encoded. */
export const aesCbcEncryptRaw = async (
  plaintext: string,
  key: CryptoKey,
  iv: Uint8Array<ArrayBuffer>
): Promise<string> => {
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    key,
    new TextEncoder().encode(plaintext)
  )
  return toBase64(cipher)
}

/**
 * Wraps the raw AES key + IV with the recipient's RSA public key, base64-encoded.
 * This is `encryptedSecret` in the payment 2FA contract. The RSA plaintext
 * is a JSON string mirroring Cerium's
 * `UnencryptedAesSecret(secretKey: ByteArray, iv: ByteArray)` — Jackson
 * serializes `ByteArray` fields as base64 strings by default.
 */
export const wrapKeyMaterial = async (
  rawKey: ArrayBuffer,
  iv: Uint8Array<ArrayBuffer>,
  pemPublicKey: string
): Promise<string> => {
  const publicKey = importPublicKey(pemPublicKey)
  const json = JSON.stringify({
    secretKey: toBase64(rawKey),
    iv: toBase64(iv.buffer),
  })
  // json is guaranteed ASCII (JSON structure + base64 values), so it's
  // already a valid forge "byte string" without a UTF-8 encoding step.
  const encrypted = publicKey.encrypt(json, 'RSAES-PKCS1-V1_5')
  return forge.util.encode64(encrypted)
}
